import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

import { MessageModel } from 'app/models/messageModel'
import { MessageType } from 'app/constants/messageType'
import { toast } from '@/lib/toasts/api'
import {
  faCircleCheck,
  faCircleExclamation,
  faTriangleExclamationSolid,
} from '@/lib/fa'

/**
 * Wystawia komunikaty ze `messageStore` na wspólny system toastów (`@/lib/toasts`).
 *
 * Wcześniej szło to przez `react-toast-notifications` (ostatnie wydanie 2022), który
 * ciągnął `react-transition-group` wołający `ReactDOM.findDOMNode` - usunięte w React 19.
 * Wywołania `showMessage(...)` w kodzie się nie zmieniły; zmienia się tylko to, przez co
 * lecą dalej. Efekt uboczny: te komunikaty wypadają teraz w tym samym rogu co toasty
 * z `@/lib/toasts` (prawy górny), a nie osobno w prawym dolnym.
 */
type Props = {
  message: MessageModel
}

const VARIANTS = {
  [MessageType.ERROR]: { variant: 'error', icon: faCircleExclamation },
  [MessageType.WARNING]: {
    variant: 'warning',
    icon: faTriangleExclamationSolid,
  },
} as const

const DEFAULT_VARIANT = { variant: 'success', icon: faCircleCheck } as const

const MessagePresenter: React.FC<Props> = ({ message }) => {
  const { t } = useLocalize()
  // Ten sam znacznik czasu = ten sam komunikat. Store zawsze ustawia świeży, więc
  // dwa identyczne komunikaty pod rząd też się pokażą.
  const lastTimestamp = React.useRef<Date>(null)

  React.useEffect(() => {
    if (message == null) return
    if (message.timestamp === lastTimestamp.current) return
    lastTimestamp.current = message.timestamp

    const { variant, icon } = VARIANTS[message.type] ?? DEFAULT_VARIANT
    const text = message.translate ? t(message.text) : message.text
    const details =
      message.details && message.translate
        ? t(message.details)
        : message.details

    toast.notify({
      variant,
      faLeadingIcon: icon,
      title: text as string,
      // Diagnostyka pustego raportu przychodzi jako lista w kolejnych liniach.
      content: details ? (
        <span className="tw-whitespace-pre-line tw-text-xs tw-opacity-80">
          {details as string}
        </span>
      ) : undefined,
      // `freeze` = komunikat zostaje, dopóki użytkownik go nie zamknie.
      autoCloseMs: message.freeze ? 0 : undefined,
    })
  }, [message, t])

  return null
}

export default MessagePresenter
